import {ForecastMapConfig} from '../../api/types';

/**
 * Interactive-overlay eligibility for the model+variable picker (#48).
 *
 * The authoritative signal is the backend-derived `config.interactive`
 * (a config renders a draped overlay iff it has exactly one paintable layer
 * on a regular lat/lon grid). Grid regularity is not derivable client-side —
 * curvilinear models (HRRR/NAM/EC_RDPS) look single-paintable but never render
 * an overlay — which is why the flag comes from the API.
 *
 * Until that field is deployed, `interactive` is absent on every payload; we
 * then fall back to the half of the predicate the client *can* see (a single
 * paintable layer). The fallback intentionally over-includes curvilinear
 * configs — they simply fall back to the flipbook when selected — and
 * self-corrects the moment the backend field lands.
 */

// Scalar (filled_contour/contour/raster) + magnitude layers carry color and
// so define the overlay; barbs / vector fields are line art that ride on top.
const PAINTABLE_LAYER_TYPES = new Set([
  'filled_contour',
  'contour',
  'raster',
  'magnitude',
]);

const isSinglePaintable = (config: ForecastMapConfig): boolean =>
  config.parameters.layers.filter((layer) =>
    PAINTABLE_LAYER_TYPES.has(layer.layer_type),
  ).length === 1;

/**
 * Fineness of a mesh string like `"0P25"` → `0.25`; smaller is finer. Used to
 * pick one config when a model exposes the same variable at several
 * resolutions (e.g. ARPEGE "Precipitation" at 0P10 and 0P25). Unparseable
 * meshes sort last.
 */
const meshFineness = (config: ForecastMapConfig): number => {
  const parsed = Number(config.forecast_model.mesh_size.replace('P', '.'));
  return Number.isFinite(parsed) ? parsed : Number.POSITIVE_INFINITY;
};

/**
 * The interactive-overlay predicate for one catalog: when the API knows
 * `interactive` (any config carries it), trust it; otherwise best-effort
 * derive it from a single paintable layer. Decided per catalog, so a payload
 * from before the field shipped is judged consistently.
 */
export const interactivePredicate = (
  configs: readonly ForecastMapConfig[],
): ((config: ForecastMapConfig) => boolean) => {
  const backendKnowsInteractive = configs.some(
    (config) => config.interactive !== undefined,
  );
  return (config) =>
    backendKnowsInteractive
      ? config.interactive === true
      : isSinglePaintable(config);
};

/** A config drawn over the model's whole grid, not cropped to a zone. */
export const isFullDomain = (config: ForecastMapConfig): boolean =>
  config.parameters.extent === null || config.parameters.extent === undefined;

/**
 * The full-domain, interactive-overlay configs the Part 1 picker offers,
 * deduped to one config per (model, variable name). Cropped configs
 * (`parameters.extent != null`) are browsed "by zone" (#50), not here.
 */
export const selectInteractiveVariables = (
  configs: ForecastMapConfig[],
): ForecastMapConfig[] => {
  const isInteractive = interactivePredicate(configs);
  const eligible = configs.filter(
    (config) => isFullDomain(config) && isInteractive(config),
  );

  const byVariable = new Map<string, ForecastMapConfig>();
  for (const config of eligible) {
    const key = `${config.forecast_model.name} ${config.name}`;
    const existing = byVariable.get(key);
    if (
      existing === undefined ||
      meshFineness(config) < meshFineness(existing)
    ) {
      byVariable.set(key, config);
    }
  }
  return [...byVariable.values()];
};

/**
 * Whether one config opens on the interactive map: full domain and
 * interactive by the catalog's predicate. Presets and the user's own builder
 * configs alike.
 *
 * Unlike `selectInteractiveVariables` there is no dedupe: a coarser mesh of
 * a variable the picker offers at a finer one is still interactive. Decides
 * where a `/forecast-maps?config=` deep link goes (#327 MR5b).
 */
export const isInteractiveForecastConfig = (
  config: ForecastMapConfig,
  catalog: readonly ForecastMapConfig[],
): boolean => isFullDomain(config) && interactivePredicate(catalog)(config);
